export type MediaUrlPlatform = {
  id: string;
  label: string;
  hosts: string[];
};

// Platforms the media API can resolve. The server still passes any https
// URL through and the API rejects the rest, but the UI only advertises these.
export const MEDIA_URL_PLATFORMS: MediaUrlPlatform[] = [
  { id: "youtube", label: "YouTube", hosts: ["youtube.com", "youtu.be"] },
  { id: "tiktok", label: "TikTok", hosts: ["tiktok.com", "vm.tiktok.com", "vt.tiktok.com"] },
  { id: "instagram", label: "Instagram", hosts: ["instagram.com"] },
  { id: "facebook", label: "Facebook", hosts: ["facebook.com", "fb.watch", "fb.com"] },
  { id: "twitter", label: "X (Twitter)", hosts: ["twitter.com", "x.com"] },
  { id: "pinterest", label: "Pinterest", hosts: ["pinterest.com", "pin.it"] },
  { id: "rutube", label: "RuTube", hosts: ["rutube.ru"] },
];

// Downloader result sources (data.source labels) that can feed the
// media-subtitle workflow. Instagram/Twitter/Pinterest never reach the results
// page, so this set only needs sources the downloader emits AND the media API
// supports.
const SUPPORTED_SOURCE_KEYS = new Set([
  "youtube",
  "tiktok",
  "facebook",
  "fb",
]);

export const normalizeMediaPageUrl = (input: string): string => {
  const value = `${input || ""}`.trim();
  if (!value) return "";

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ? value : `https://${value}`;

  try {
    const url = new URL(withScheme);
    if (url.protocol !== "https:" && url.protocol !== "http:") return "";
    if (!url.hostname.includes(".")) return "";
    return url.toString();
  } catch {
    return "";
  }
};

// Coerce clipboard text into a usable http(s) link, or null when it is not one.
// Already-schemed URLs are kept verbatim; bare domains get https:// via
// normalizeMediaPageUrl so downstream /^https?:\/\/\S+/i submit gates pass.
export const coercePastedLink = (raw: string): string | null => {
  const text = `${raw || ""}`.trim();
  if (!text || /\s/.test(text)) return null;
  if (/^https?:\/\/\S+$/i.test(text)) return text;
  return normalizeMediaPageUrl(text) || null;
};

const matchesHost = (hostname: string, host: string) =>
  hostname === host || hostname.endsWith(`.${host}`);

export const detectMediaUrlPlatform = (input: string): MediaUrlPlatform | null => {
  const normalized = normalizeMediaPageUrl(input);
  if (!normalized) return null;

  try {
    const hostname = new URL(normalized).hostname.toLowerCase();
    return (
      MEDIA_URL_PLATFORMS.find((platform) =>
        platform.hosts.some((host) => matchesHost(hostname, host))
      ) || null
    );
  } catch {
    return null;
  }
};

export const buildSupportedPlatformsLabel = (limit = 5): string =>
  MEDIA_URL_PLATFORMS.slice(0, limit)
    .map((platform) => platform.label)
    .join(", ");

export const isMediaSubtitleSupportedSource = (
  source: string | number,
  url?: string
): boolean => {
  const key = `${source || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (SUPPORTED_SOURCE_KEYS.has(key)) return true;
  return Boolean(url && detectMediaUrlPlatform(url));
};
